// The one data layer every screen uses. Screens never call Supabase directly.
import { createClient, type Session } from '@supabase/supabase-js'
import { categoryText } from '../ui/CategoryLabel'
import * as Sentry from '@sentry/react'
import { SUPABASE_URL, SUPABASE_ANON_KEY, APP_URL, fn } from '../config'
import type { ExploreRow } from './explore'

// R-52: what a Google redirect brought back, read before the client below consumes the address (on an error it keeps
// the session, but the error itself is only in the URL). Taken once by lib/signin.ts.
let authReturn: { error: string | null } | null = (() => {
  try {
    const params = new URLSearchParams(location.hash.replace(/^#/, ''))
    new URLSearchParams(location.search).forEach((v, k) => { if (!params.has(k)) params.set(k, v) })
    const error = params.get('error_code') || params.get('error')
    if (!error && !params.get('access_token')) return null
    if (error) {
      const u = new URL(location.href)
      for (const k of ['error', 'error_code', 'error_description', 'sb']) u.searchParams.delete(k)
      history.replaceState(history.state, '', u.pathname + u.search)
    }
    return { error }
  } catch {
    return null
  }
})()
export function takeAuthReturn(): { error: string | null } | null {
  const r = authReturn
  authReturn = null
  return r
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: { persistSession: true, autoRefreshToken: true, storageKey: 'magellan-auth' },
})

/** Public item columns. `contributed_by` is server-only (column grants) and must never be requested. */
export const ITEM_COLUMNS = 'id,kind,title,venue_name,address,neighborhood,lat,lng,start_at,end_at,price_text,is_free,cost_tier,category,tags,one_line,url,source_name,iykyk_score,size_hint,rsvp,vibe'

export type Item = {
  id: string
  kind: 'activity' | 'event'
  title: string
  venue_name: string | null
  address: string | null
  neighborhood: string | null
  lat: number | null
  lng: number | null
  start_at: string | null
  end_at: string | null
  price_text: string | null
  is_free: boolean | null
  cost_tier: 'free' | 'cheap' | 'mid' | 'splurge' | null
  category: string
  tags: string[]
  one_line: string | null
  url: string
  source_name: string | null
  iykyk_score: number
  size_hint: string | null
  /** V2: 'approval' = the host approves each RSVP on their own page (tech week and similar); absent = open. */
  rsvp?: 'open' | 'approval' | null
  /** Photos step 1: the source's own image (Ticketmaster, tech week, a venue's link preview) and who to credit. */
  image_url?: string | null
  image_credit?: string | null
  /** Google Places hybrid (10/09): the exact Google place id of a map place (free IDs-only match); the Maps link uses it. */
  google_place_id?: string | null
  /** Judged personality dimensions, each -1..1 (energy, posture, structure, novelty, lateness, commitment, sociality). */
  vibe?: { dims?: Record<string, number> } | null
}

/**
 * from_deck: the person's own swipe on this item in the deck (2 love, 1 like, -1 meh), when there was one.
 * my_vote / partner_vote: couple yes (1) / not this time (-1). night_ok: the server says this pick can anchor a night.
 * All three are optional: older servers don't send them, and the client hides what they drive.
 */
export type Vote = 1 | -1
export type Pick = {
  rank: number; why: string; item: Item; from_deck?: 2 | 1 | -1 | null
  my_vote?: Vote | null; partner_vote?: Vote | null; night_ok?: boolean
}
export type PicksResponse = { batch_id: string; created_at: string; couple?: boolean; picks: Pick[]; reused?: boolean }

export type Profile = {
  id: string
  mode: 'solo' | 'couple'
  display_name: string | null
  home_area: string | null
  home_lat: number | null
  home_lng: number | null
  max_drive_min: number | null
  budget: 'free' | 'cheap' | 'mid' | 'splurge' | null
  hard_nos: string[]
  perfect_saturday: string | null
  iykyk_pref: number
  couple_id: string | null
  intake_done_at: string | null
  /** What this week is for and when (written by intake and by seed-taste). */
  week_context?: WeekContext | null
}

export type SwipeValue = -2 | -1 | 1 | 2 // no, meh, like, love

// ---------- session ----------
export async function ensureSession(): Promise<Session> {
  const { data } = await supabase.auth.getSession()
  if (data.session) return data.session
  const { data: anon, error } = await supabase.auth.signInAnonymously()
  if (error || !anon.session) throw error ?? new Error('could not start a session')
  track('session_started', {})
  return anon.session
}

async function userId(): Promise<string> {
  if (signinHold.on) throw new ApiError('finishing sign-in', null)
  const s = await ensureSession()
  Sentry.setUser({ id: s.user.id })
  return s.user.id
}

// ---------- profile ----------
export async function getProfile(): Promise<Profile | null> {
  const id = await userId()
  const { data, error } = await supabase.from('profiles').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  return data as Profile | null
}

export async function saveProfile(p: Partial<Omit<Profile, 'id' | 'couple_id'>>): Promise<Profile> {
  const id = await userId()
  const { data, error } = await supabase.from('profiles').upsert({ id, ...p }, { onConflict: 'id' }).select('*').single()
  if (error) throw error
  return data as Profile
}

// ---------- the landing example ----------
export type LandingExample = { id: string; title: string; category: string; tags: string[] | null; start_at: string | null; iykyk_score?: number | null }

/**
 * Three real upcoming happenings for the landing's example cards, read with the public anon key (no session is
 * created). Different categories where the pool allows; short titles first so they fit a small card.
 * Only dated events: on-demand cities hold places, not events, so these are Los Angeles happenings.
 */
export async function getLandingExamples(): Promise<LandingExample[]> {
  const now = new Date()
  const soon = new Date(now.getTime() + 14 * 86400e3)
  const { data, error } = await supabase
    .from('items')
    .select('id,title,category,tags,start_at,iykyk_score')
    .eq('status', 'live')
    .eq('kind', 'event')
    .gte('start_at', now.toISOString())
    .lte('start_at', soon.toISOString())
    .order('start_at')
    .limit(250)
  if (error) throw error
  // a catch-all category only when a tag gives it a real label (the card shows the category mark)
  const fit = ((data ?? []) as LandingExample[]).filter(
    (r) => r.title && r.start_at && r.title.length <= 44 && (r.category !== 'other' || categoryText(r.category, r.tags) !== 'Other'),
  )
  // R9: the small, one-off things (iykyk >= 0.6) first, any upcoming after; soonest first within each
  const iykyk = (r: LandingExample) => (r.iykyk_score ?? 0) >= 0.6
  const rows = [...fit.filter(iykyk), ...fit.filter((r) => !iykyk(r))]
  const laDay = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' })
  const day = (r: LandingExample) => laDay.format(new Date(r.start_at!))
  // different categories and, where the pool allows, different days (soonest first either way)
  for (const spreadDays of [true, false]) {
    const out: LandingExample[] = []
    for (const r of rows) {
      if (out.length === 3) break
      if (out.some((o) => o.category === r.category || (spreadDays && day(o) === day(r)))) continue
      out.push(r)
    }
    if (out.length === 3) return out.sort((a, b) => a.start_at!.localeCompare(b.start_at!))
  }
  return []
}

// ---------- the swipe deck ----------
/** The live pool for the adaptive deck (public columns only). */
export async function getPool(): Promise<Item[]> {
  // The API returns at most 1000 rows per request: page through in a stable order until a short page.
  const PAGE = 1000
  const out: Item[] = []
  let columns = ITEM_COLUMNS
  for (let from = 0; from < 20 * PAGE; from += PAGE) {
    let { data, error } = await supabase.from('items').select(columns).eq('status', 'live').order('id').range(from, from + PAGE - 1)
    if (error?.code === '42703' && columns.endsWith(',vibe')) {
      // a database without the vibe column yet: the deck works without it
      columns = columns.slice(0, -',vibe'.length)
      ;({ data, error } = await supabase.from('items').select(columns).eq('status', 'live').order('id').range(from, from + PAGE - 1))
    }
    if (error) throw error
    const rows = (data ?? []) as unknown as Item[]
    out.push(...rows)
    if (rows.length < PAGE) break
  }
  return out
}

/**
 * The server-made starting weights from the taste step (talk or tap), which the deck uses as its prior, and the
 * week as the server read it (days, latest hour, daytime said in words land here).
 */
export async function getTasteSeed(): Promise<{ weights: Record<string, number>; week: WeekContext | null }> {
  const id = await userId()
  const { data } = await supabase.from('profiles').select('taste_seed,week_context').eq('id', id).maybeSingle()
  const row = data as { taste_seed?: Record<string, number> | null; week_context?: WeekContext | null } | null
  return { weights: row?.taste_seed ?? {}, week: row?.week_context ?? null }
}

/** Why a No or "Not for me" (one tap, optional). Matches the swipes.reason check constraint. */
export const WHY_NOT = ['too_far', 'too_late', 'too_pricey', 'wrong_day', 'wrong_vibe', 'not_my_thing', 'been_there'] as const
export type WhyNot = (typeof WHY_NOT)[number]
/** The menu's extra logistics reasons (R6): accepted once the swipes.reason constraint carries them. */
export const MENU_WHY_NOT = ['not_for_kids', 'too_long', 'too_crowded'] as const
export type Reason = WhyNot | (typeof MENU_WHY_NOT)[number]

/** Upsert the person's own swipe. A reason is only sent when given, so a later save without one keeps it. */
export async function saveSwipe(itemId: string, value: SwipeValue, reason?: Reason): Promise<void> {
  const id = await userId()
  const row = { user_id: id, item_id: itemId, value, ...(reason ? { reason } : {}) }
  const { error } = await supabase.from('swipes').upsert(row, { onConflict: 'user_id,item_id' })
  if (error) throw error
}

/** Take an answer back (an undone card that was never answered again). */
export async function deleteSwipe(itemId: string): Promise<void> {
  const id = await userId()
  const { error } = await supabase.from('swipes').delete().eq('user_id', id).eq('item_id', itemId)
  if (error) throw error
}

// ---------- server functions ----------
/** A server function failure; status is the HTTP status when there was a response (404 = not deployed yet). */
export class ApiError extends Error {
  status: number | null
  constructor(message: string, status: number | null) {
    super(message)
    this.status = status
  }
}

/**
 * R-52: on while a sign-in that came back from Google still has its merge to finish (set by lib/signin.ts, in every tab
 * of this browser). Nothing but the merge itself may run as the new account until then, or the server would give a
 * brand-new account an empty profile and draft that outrank the visitor's.
 */
export const signinHold = { on: false }

export async function callFn<T>(name: string, body: Record<string, unknown>): Promise<T> {
  if (signinHold.on && name !== 'account') throw new ApiError('finishing sign-in', null)
  await ensureSession()
  const { data, error } = await supabase.functions.invoke(fn(name), { body })
  if (error) {
    let message = error.message
    let status: number | null = null
    try {
      const ctx = (error as { context?: Response }).context
      if (ctx) {
        status = ctx.status ?? null
        message = (await ctx.json()).error ?? message
      }
    } catch { /* keep default message */ }
    throw new ApiError(message, status)
  }
  return data as T
}

export const getPicks = (refresh = false) => callFn<PicksResponse>('picks', { refresh })

/** The whole week ranked for this person (up to 400), filtered in the app (lib/explore.ts). */
export const getExplore = () => callFn<{ items: ExploreRow[] }>('picks', { explore: true })

/** One new pick in place of a passed one; null when nothing else fits (404) or the call fails. */
export async function replacePick(itemId: string): Promise<Pick | null> {
  try {
    const res = await callFn<{ pick?: Pick | null }>('picks', { replace: itemId })
    return res?.pick?.item ? res.pick : null
  } catch {
    return null
  }
}

// ---------- couple votes ----------
/** Yes / not this time on a couple pick, or null to clear it. Own row only (RLS). A vote is never a swipe. */
export async function setVote(batchId: string, itemId: string, vote: Vote | null): Promise<void> {
  const id = await userId()
  const { error } = await supabase.from('picks').update({ vote }).eq('user_id', id).eq('batch_id', batchId).eq('item_id', itemId)
  if (error) throw error
}

// ---------- make a night of it (build/CLIENT-NEXT.md contract) ----------
export type StopKind = 'food' | 'drink' | 'coffee' | 'dessert' | 'walk' | 'view' | 'activity'
export type Stop = {
  id: string
  source: 'item' | 'osm'
  name: string
  kind: StopKind
  lat: number
  lng: number
  address: string | null
  neighborhood: string | null
  url: string | null
  hours_text: string | null
  open_label: string | null
  cost_tier: Item['cost_tier']
  tags: string[]
  dims?: Record<string, number>
  label: 'fit' | 'find' | 'wildcard' | 'nearby'
  leg: 'walk' | 'drive'
  leg_min: number
  start: string
  end: string
  /** One factual line. */
  line: string
}
/** A null `chosen` with a reason means the slot isn't possible; alternates are at most 8. */
export type NightSlot = { reason?: string; chosen: Stop | null; alternates: Stop[] }
export type NightPlan = {
  anchor: { item: Item; start: string; end: string }
  ok: boolean
  reason?: string
  slots: { before: NightSlot | null; after: NightSlot | null }
  latest_hour?: number
}
export type SavedStop = { id: string; source: 'item' | 'osm'; name: string; start: string; end: string; address: string | null; url: string | null }
export type SavedNight = { id: string; stops: SavedStop[]; couple: boolean }

/**
 * Build a night around an anchor. With `plan`, the server treats what is already in that plan's day as busy time
 * (before / after stops never overlap it; a slot with no room comes back empty with its `reason`). A server that
 * refuses the plan field (400) is asked again without it.
 */
export async function buildNight(anchor: string, plan?: string): Promise<NightPlan> {
  if (!plan) return callFn<NightPlan>('night', { anchor })
  try {
    return await callFn<NightPlan>('night', { anchor, plan })
  } catch (e) {
    if (e instanceof ApiError && e.status === 400) return callFn<NightPlan>('night', { anchor })
    throw e
  }
}
export const saveNight = (save: { anchor: string; before?: string; after?: string; surprise?: boolean }, nightId?: string) =>
  callFn<{ night: SavedNight }>('night', { save, ...(nightId ? { night_id: nightId } : {}) })
export const listNights = () => callFn<{ nights: SavedNight[] }>('night', { list: true })

/**
 * Ask in plain words ("live jazz tonight"): what the server understood and up to 12 things, in its order. Each carries
 * the time it would suggest (`suggested`, a time they named when it can) and the area `km` is from. `loose`: nothing
 * matched exactly, these come closest. 429 = daily limit, 400 = too short.
 */
export type AskItem = { score: number; km: number | null; item: Item; suggested?: { start: string; end: string; approx: boolean } | null; from?: string | null }
export type AskResponse = { understood: string; filters: Record<string, unknown>; items: AskItem[]; loose?: boolean }
/** `planId`: search within that plan's city and dates (a Lisbon plan never answers with Los Angeles). */
export const ask = (q: string, planId?: string) => callFn<AskResponse>('ask', planId ? { q, plan: planId } : { q })

/** Share a saved night (owner only): a link token; `off` revokes it. */
export const shareNight = (nightId: string) => callFn<{ share_token: string }>('night', { share: nightId })
export const unshareNight = (nightId: string) => callFn<{ ok?: boolean }>('night', { share: nightId, off: true })

export type SharedStop = {
  slot: 'before' | 'anchor' | 'after'; name: string; start: string; end: string
  address: string | null; url: string | null; lat: number | null; lng: number | null; source: 'item' | 'osm' | string
}
/**
 * A shared night, read with the public key only: no session is read or created, so a friend opening the link
 * never becomes an anonymous user. null = the link was turned off or never existed (404).
 */
export async function getSharedNight(token: string): Promise<{ stops: SharedStop[] } | null> {
  const r = await fetch(`${SUPABASE_URL}/functions/v1/${fn('night')}`, {
    method: 'POST',
    headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ shared: token }),
  })
  if (r.status === 404) return null
  if (!r.ok) {
    let message = `night ${r.status}`
    try { message = (await r.json()).error ?? message } catch { /* keep */ }
    throw new ApiError(message, r.status)
  }
  const data = (await r.json()) as { night?: { stops?: SharedStop[] } }
  return { stops: Array.isArray(data?.night?.stops) ? data.night.stops : [] }
}

export type Guess = 'love' | 'like' | 'meh' | 'no'
export type GuessCard = { item: Item; guess: Guess; why: string }
/** Predict-then-correct intake: after the profile (incl. perfect_saturday) is saved, get ~10 cards with our guess for each. */
/** Tap path for the taste step (the alternative to talking). ids must match supabase/functions/_shared/vibes.ts */
export const VIBES: { id: string; label: string }[] = [{"id": "music", "label": "Live music"}, {"id": "food", "label": "Tiny restaurants"}, {"id": "drinks", "label": "Drinks & bars"}, {"id": "art", "label": "Art & galleries"}, {"id": "comedy", "label": "Comedy"}, {"id": "film", "label": "Film nights"}, {"id": "books", "label": "Books & talks"}, {"id": "hikes", "label": "Hikes & views"}, {"id": "beach", "label": "Beach days"}, {"id": "markets", "label": "Markets & makers"}, {"id": "classes", "label": "Classes & workshops"}, {"id": "wellness", "label": "Calm & wellness"}, {"id": "daytrips", "label": "Day trips"}, {"id": "weird", "label": "Something weird"}, {"id": "active", "label": "Active & sporty"}, {"id": "late", "label": "Late nights"}]

/**
 * What this week is for and when. days: 0=Sun..6=Sat (tapped with when='days', or said in words).
 * latest_hour (24h, "home by 10" = 22) and daytime_ok come from what they said.
 */
export type WeekContext = {
  occasion: 'date' | 'solo' | 'friends' | 'new' | string
  when: 'weekend' | 'weeknight' | 'any' | 'days' | string
  days?: number[] | null
  latest_hour?: number | null
  daytime_ok?: boolean | null
}
export const seedTaste = (opts: { week?: WeekContext; vibes?: string[]; budget?: Profile['budget']; max_drive_min?: number } = {}) => callFn<{ guesses: GuessCard[]; seeded: number; skipped?: string | null }>('seed-taste', opts)
export const guessToValue = (g: Guess): SwipeValue => ({ love: 2, like: 1, meh: -1, no: -2 } as const)[g]
export const contribute = (input: string, wantsCredit = false) =>
  callFn<{ status: 'accepted' | 'pending' | 'duplicate' | 'rejected'; reason: string; item_id: string | null }>('contribute', { input, wants_credit: wantsCredit })
export type PortraitLine = { id: number; text: string; basis: string; verdict?: 'yes' | 'no' | 'kind of' }
/** The 4-line taste portrait shown after the deck (~10s; prefetch it around card 6). */
export const getPortrait = () => callFn<{ lines: PortraitLine[] }>('portrait', {})
/** Save yes / no / kind of on each portrait line; these feed future picks. */
export const sendPortraitVerdicts = (verdicts: Record<string, 'yes' | 'no' | 'kind of'>) => callFn<{ lines: PortraitLine[] }>('portrait', { verdicts })

export const createInvite = () => callFn<{ code: string }>('couple', { action: 'create' })
export const joinCouple = (code: string) => callFn<{ ok: boolean }>('couple', { action: 'join', code })
export const leaveCouple = () => callFn<{ ok: boolean }>('couple', { action: 'leave' })
export async function deleteMe(): Promise<void> {
  await callFn('delete-me', {})
  await supabase.auth.signOut()
  localStorage.clear()
}
export const inviteLink = (code: string) => `${APP_URL}/join/${code}`

// ---------- feedback after a calendar add ----------
/** Items the user put on their calendar whose time has passed and that have no feedback yet. */
export async function getPendingFeedback(): Promise<Item[]> {
  const id = await userId()
  const [{ data: adds }, { data: fb }] = await Promise.all([
    supabase.from('calendar_adds').select(`item_id, event_end, item:items(${ITEM_COLUMNS})`).eq('user_id', id),
    supabase.from('feedback').select('item_id').eq('user_id', id),
  ])
  const done = new Set((fb ?? []).map((f) => f.item_id))
  const now = Date.now()
  const out: Item[] = []
  for (const a of (adds ?? []) as unknown as { item_id: string; event_end: string | null; item: Item | null }[]) {
    if (!a.item || done.has(a.item_id)) continue
    // Over when the calendar block we made is over (for an undated activity that's the suggested slot);
    // with no time at all we don't ask.
    const t = a.event_end ?? a.item.end_at ?? a.item.start_at
    const past = t ? new Date(t).getTime() < now : false
    if (past && !out.find((i) => i.id === a.item_id)) out.push(a.item)
  }
  return out
}

export async function sendFeedback(itemId: string, rating: 'loved' | 'fine' | 'meh' | 'skipped', heardBefore: boolean | null, tip?: string) {
  const id = await userId()
  const { error } = await supabase.from('feedback').upsert(
    { user_id: id, item_id: itemId, rating, heard_before: heardBefore, tip: tip?.slice(0, 280) || null },
    { onConflict: 'user_id,item_id' },
  )
  if (error) throw error
  track('feedback', { item_id: itemId, rating, heard_before: heardBefore })
}

// ---------- analytics (habit tests) ----------
export function track(name: string, props: Record<string, unknown>) {
  supabase.auth.getSession().then(({ data }) => {
    const uid = data.session?.user.id
    if (!uid) return
    supabase.from('app_events').insert({ user_id: uid, name, props }).then(({ error }) => {
      if (error) Sentry.captureException(error)
    })
  })
}

// ---------- LA neighborhoods for the home-area picker (approximate centers) ----------
export const AREAS: { name: string; lat: number; lng: number }[] = [
  { name: 'Atwater Village', lat: 34.118, lng: -118.262 },
  { name: 'Burbank / Glendale', lat: 34.165, lng: -118.28 },
  { name: 'Culver City', lat: 34.021, lng: -118.396 },
  { name: 'Downtown / Arts District', lat: 34.043, lng: -118.243 },
  { name: 'East LA / Boyle Heights', lat: 34.033, lng: -118.19 },
  { name: 'Highland Park / Eagle Rock', lat: 34.116, lng: -118.193 },
  { name: 'Hollywood', lat: 34.098, lng: -118.326 },
  { name: 'Inglewood / Westchester', lat: 33.962, lng: -118.353 },
  { name: 'Koreatown', lat: 34.061, lng: -118.3 },
  { name: 'Long Beach', lat: 33.77, lng: -118.19 },
  { name: 'Los Feliz', lat: 34.106, lng: -118.284 },
  { name: 'Mid-City / Miracle Mile', lat: 34.064, lng: -118.35 },
  { name: 'Mount Washington / Glassell Park', lat: 34.11, lng: -118.225 },
  { name: 'North Hollywood / Valley Village', lat: 34.172, lng: -118.379 },
  { name: 'Pasadena', lat: 34.148, lng: -118.144 },
  { name: 'San Gabriel Valley', lat: 34.096, lng: -118.106 },
  { name: 'Santa Monica', lat: 34.019, lng: -118.491 },
  { name: 'Silver Lake / Echo Park', lat: 34.087, lng: -118.27 },
  { name: 'South Bay', lat: 33.86, lng: -118.39 },
  { name: 'South LA / Leimert Park', lat: 34.004, lng: -118.33 },
  { name: 'Studio City / Sherman Oaks', lat: 34.15, lng: -118.42 },
  { name: 'Venice / Mar Vista', lat: 33.99, lng: -118.46 },
  { name: 'West Hollywood', lat: 34.09, lng: -118.361 },
  { name: 'West LA / Westwood', lat: 34.05, lng: -118.44 },
]
